<template>
  <div class="credentials-card-container" v-bind:class="{ loading: saving }">
    <div class="credentials-card" v-bind:class="{ loading: saving, error: error}">
      <div class="bank-title" :class="{ 'no-instructions': !source.instructions }">
        <span>
          {{ source.displayName }}
        </span>
        <div v-if="!disableRemove" class="remove-container"
             :class="{ 'save-in-process': savingInProcess }"
             v-on="savingInProcess ? {} : { click: () => remove('X') }">
          <img :src="require('@/assets/icons/close-fat.svg')">
        </div>
      </div>
      <div v-if="source.instructions" class="instructions-container">
        <component :is="instructions" v-if="!hideInstructions"></component>
      </div>
      <div class="card-bottom" :class="{ error }">
        <ValidationObserver ref="observer" slim>
          <div class="input-wrapper">
            <text-input-field class="customer-name-field"
                              :disabled="false"
                              :error="source.validations.nameError"
                              :fieldText="'של מי החשבון? (שם)'"
                              :fieldValue="source.name"
                              :autofocus="focusOnLoad"
                              :updateInput="onNameInput(source.credentialsId)"/>
            <hr class="devider">
            <div v-for="(field, fieldKey) in source.fields" :key="fieldKey">
              <text-input-field v-if="field.type !== 'password'"
                                ref="field"
                                :fieldText="field.name"
                                :fieldValue="source.fields[fieldKey].value"
                                :updateInput="updateInput(source.credentialsId, fieldKey)"
                                :validations="field.validation"
              />
              <password-input-field v-else
                                    ref="field"
                                    :fieldText="field.name"
                                    :fieldValue="source.fields[fieldKey].value"
                                    :updateInput="updateInput(source.credentialsId, fieldKey)"
                                    :validations="field.validation"
              />
            </div>
          </div>
          <div class="forgot-password-link"
                            v-if="this.source.links.forgotPassword"
                            v-on:click="openForgotPasswordLink()">
            שכחתי את פרטי המשתמש שלי
          </div>
          <riseup-button v-if="this.source.sourceName === 'דיסקונט ישיר'"
                       :disabled="!saveEnabled || saving" :title="'המשך לאתר דיסקונט'" :variant="'discount'" :action="saveDiscountDirect"/>
          <riseup-button v-else
                       :disabled="!saveEnabled || saving" :title="'שמירת פרטים'" :variant="'primary'" :action="save"/>
        </ValidationObserver>
      </div>
      <credentials-validation-error :checkCredentials="source.validations.checkCredentials"
                                    :sourceName="source.sourceName"
                                    :links="source.links"
                                    v-if="credentialsError">
      </credentials-validation-error>
      <discount-switch-to-api-connection v-if="this.source.sourceName === 'דיסקונט'" :source="source"/>
      <div v-if="!disableRemove" class="remove-container-text" v-on:click="remove('text')">
        מחיקת החשבון
      </div>
      <card-error v-if="this.updateError" :error-message="'תקלה בשמירת הנתונים. נסו שנית.'"/>
      <bank-footer :source="source" />
    </div>
  </div>
</template>

<script>

import _ from 'lodash';
import { mapState, mapActions } from 'vuex';
import BaseUI from 'base-ui';
import Segment from '../../../Segment';
import TextInputField from '../../card-parts/TextInputField.vue';
import PasswordInputField from '../../card-parts/PasswordInputField.vue';
import CardButton from '../../card-parts/CardButton.vue';
import CredentialsValidationError from './CredentialsValidationError.vue';
import CardError from '../../card-parts/CardError.vue';
import ModalBus from '../../../event-bus/modal-bus';
import RedirectToDiscountPopup from '../../../pages/responsive-pages/non-authenticated/discount-api/RedirectToDiscountPopup.vue';
import BankFooter from './BankFooter.vue';
import DiscountSwitchToApiConnection from './discount/DiscountSwitchToApiConnection.vue';

export default {
  name: 'NewCredentialsCard',
  props: [
    'focusOnLoad',
    'source',
    'saving',
    'savingInProcess',
  ],
  data() {
    return {
      disableRemove: false,
    };
  },
  components: {
    CardError,
    CredentialsValidationError,
    TextInputField,
    PasswordInputField,
    CardButton,
    RiseupButton: BaseUI.RiseupButton,
    BankFooter,
    DiscountSwitchToApiConnection,
  },
  async created() {
    Segment.trackUserGot('NewCredentialsCard_Shown', { sourceName: this.source.sourceName });
  },
  computed: {
    ...mapState('credentials', ['runAfterSave', 'credsToAccounts', 'loadingCreds']),
    ...mapState('websocket', ['socketMessage']),
    saveEnabled() {
      const isCustomerNameValid = this.source.name.length > 1;
      return isCustomerNameValid && _.every(this.source.fields, field => field.value && field.value.length > 3);
    },
    instructions() {
      return () => this.source.instructions ? import(`./card-instructions/${this.source.instructions}`) : null; // eslint-disable-line
    },
    updateError() {
      return this.source.updateError;
    },
    credentialsError() {
      return !this.source.validations.checkCredentials.isValid;
    },
    error() {
      return this.updateError || this.credentialsError;
    },
    hideInstructions() {
      return (!this.source.validations.checkCredentials.isValid
        && ['PasswordExpired', 'AccountBlocked', 'AccountNotFound'].includes(this.source.validations.checkCredentials.reason));
    },
  },
  methods: {
    ...mapActions('credentials', ['updateCredentialsField', 'updateCredentials', 'updateCredentialsStatus',
      'deleteCredentials', 'setName', 'setNameError', 'fetchCredsToAccounts', 'fetchCredsToAccounts']),
    async save() {
      const valid = await this.$refs.observer.validate();
      _.forEach(this.$refs.field, field => field.onSave());
      if (valid) {
        Segment.trackUserInteraction('AccountSaveClicked', { name: this.source.sourceName });
        Segment.trackUserInteractionToPixel('ASC');
        try {
          this.$emit('saving-started');
          const result = await this.updateCredentials({ source: this.source });
          if (result.success === true) {
            if (!this.runAfterSave) {
              this.$emit('saving-completed');
            }
          } else {
            this.$emit('saving-completed-with-error');
          }
        } catch (error) {
          this.$emit('saving-completed-with-error');
        }
      }
    },
    async saveDiscountDirect() {
      const valid = await this.$refs.observer.validate();
      _.forEach(this.$refs.field, field => field.onSave());
      if (valid) {
        Segment.trackUserInteraction('AccountSaveClicked', { name: this.source.sourceName });
        Segment.trackUserInteractionToPixel('ASC');
        const idNumber = this.source.fields.idNumber.value;
        ModalBus.$emit('open', {
          component: RedirectToDiscountPopup,
          props: {
            source: this.source,
            idNumber,
          },
        });
      }
    },
    async remove(buttonName) {
      this.disableRemove = true;
      try {
        Segment.trackUserInteraction('RemoveAccountClicked', {
          name: this.source.sourceName,
          button: buttonName,
        });
        await this.deleteCredentials({ credentialsId: this.source.credentialsId });
      } catch (e) {
        this.disableRemove = false;
        throw e;
      }
    },
    onNameInput(credentialsId) {
      return value => {
        this.setNameError({ credentialsId, error: '' });
        this.setName({ credentialsId, name: value });
      };
    },
    updateInput(credentialsId, fieldKey) {
      return value => this.updateCredentialsField({ credentialsId, fieldKey, value });
    },
    openForgotPasswordLink() {
      Segment.trackUserInteraction('CardForgotPassowrdClicked', {
        source: this.source.sourceName,
      });
      window.open(this.source.links.forgotPassword, '_blank');
    },
  },
};
</script>

<style lang="scss" scoped>
  @import '../../../../node_modules/base-ui/src/scss/riseup-colors';
  @import '../../../scss/mixins';

  .credentials-card-container {
    position: relative;

    &.loading {
      border-radius: 3px;
      border: 2px solid $riseup_light_blue;
    }

    .credentials-card {
      width: 100%;
      border-radius: 3px;
      box-shadow: 2px 2px 8px 0 $riseup_gray_1;
      display: flex;
      flex-direction: column;
      align-items: flex-start;

      &.loading {
        opacity: 0.05;
      }

      &.error {
        border-radius: 3px;
        border: 2px solid $riseup_coral;
      }

      .bank-title {
        @include card-title;
        margin-bottom: 11px;
        width: 100%;
        display: flex;
        justify-content: space-between;
        padding: 0 30px;
        padding-top: 24px;

        &.no-instructions {
          margin-bottom: 22px;
        }

        .nickname {
          font-size: 20px;
          font-weight: normal;
          font-family: SimplerPro;
        }

        .thanks {
          font-weight: normal;
          font-family: SimplerPro;
          font-size: 20px;
          color: $riseup_green;
          margin: auto 0;
        }

        .remove-container {
          position: absolute;
          top: 9px;
          left: 10px;
          cursor: pointer;

          &.save-in-process {
            opacity: 0.3;
            cursor: default;
          }

          img {
            height: 23px;
          }
        }
      }

      .remove-container-text {
        text-align: center;
        color: $riseup_light_blue;
        text-decoration: underline;
        cursor: pointer;
        margin: 25px auto;
        font-family: SimplerProBold;
      }

      .instructions-container {
        padding: 0 30px 22px;
        font-size: 20px;
        font-weight: normal;
        line-height: 1.2em;
        font-family: SimplerPro;
      }

      .card-bottom {
        width: 100%;
        padding: 0 30px 0;

        .input-wrapper {
          margin-bottom: 30px;
          .customer-name-field {
            margin-bottom: 8px;
          }
          .devider {
            margin: 20px 0;
            border-color: $riseup_gray_0;
            border-width: 0.5px;
            border-style: solid;
          }
        }

        &.error {
          border-bottom: none;
        }

        .forgot-password-link {
          margin-bottom: 30px;
          color: $riseup_light_blue;
          font-size: 16px;
          font-weight: bold;
          text-decoration: underline;
          text-align: center;
          cursor: pointer;
        }

        .riseup-button {
          width: 100%;
        }
      }

      .input-container {
        height: 40px;
        margin-bottom: 20px;

        .input-group {
          padding: 0 30px;
          width: 100%;
          height: 40px;
        }
      }
    }
  }
</style>
